import { Icon } from '@iconify/react'
import { Computed, useObservable } from '@legendapp/state/react'
import { formOptions, useForm } from '@tanstack/react-form'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import { zodValidator } from '@tanstack/zod-form-adapter'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { z } from 'zod'

import { changeDistributorEmail, fetchDistributorDetails, updateDistributor } from '~features/distributors/api'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger, Button, Card, CardContent, CardFooter, DialogComponent, IconButton, Input, Label, PhoneInput, Separator, Textarea } from '~in-app/components'
import Dropdown from '~in-app/components/Dropdown'
import { auth$ } from '~in-app/legend-state'
import { fetchCountryList } from '~in-app/shareable/index.api'
import { addressTypes, Contact, contactTypes, PersonsInCharge } from '~in-app/types'
import { updateDistributorValidation } from '~in-app/validation/distributor.validation'

export const Route = createFileRoute('/console/distributor/edit/$id')({
  component: EditDistributorPage,
  staticData: {
    privileges: ['0.3.7.2'],
  },
})

function EditDistributorPage() {
  const router = useRouter()
  const state$ = useObservable({
    // countryList: [] as Option[],
    newAdminEmail: '',
    emailError: null as string | null,
    // data: {} as Distributor,
    isOpen: false,
  })
  const emailSchema = z.string().min(1, 'Email is required').email('Invalid email format')
  const { id } = Route.useParams()

  const { data: data } = useQuery({
    queryKey: ['distributor-details'],
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      const response = await fetchDistributorDetails({
        id,
        abortCtrl: signal,
      })
      return response?.[0] || null
    },
  })

  const queryClient = useQueryClient()

  const changeEmailFunc = () => {
    const mutation = useMutation({
      mutationFn: ({ id, email }: { id: string, email: string }) => changeDistributorEmail(id, email),
      onSuccess: () => {
        toast.success(`Email Changed Successfully`, {
          position: 'top-center',
          hideProgressBar: false,
          autoClose: 8000,
        })
        queryClient.invalidateQueries({ queryKey: ['distributor-details'] })
        state$.isOpen.set(false)
      },
      onError: (error: Error) => {
        toast.error(`Error Fetching Distributor: ${error}`, { position: 'top-center', hideProgressBar: false, autoClose: 500 })
      },
    })

    return mutation
  }
  const changeEmailMutation = changeEmailFunc()

  const { data: countryList = [] } = useQuery({
    queryKey: ['add-distributor-country-listing'],
    queryFn: async () => {
      const response = await fetchCountryList()
      const countryList = response?.list?.map((item: any) => ({
        label: item.name,
        value: item.id,
      })) ?? []
      return countryList
    },
  })

  const updateDistributorFunc = () => {
    const mutation = useMutation({
      mutationFn: updateDistributor,
      onSuccess: () => {
        toast.success(`Record Updated Successfully`, {
          position: 'top-center',
          hideProgressBar: false,
          autoClose: 8000,
        })

        router.navigate({ to: '/console/distributor/list' })
      },
      onError: (error: Error) => {
        console.error('Error occurred:', error)
      },
    })

    return mutation
  }
  const updateDistributorMutation = updateDistributorFunc()

  const submitDistributor = async (value: any) => {
    try {
      const submitValue = JSON.parse(JSON.stringify(value))
      if (submitValue.personsInCharge.length === 0) {
        delete submitValue.personsInCharge
      }
      if (submitValue.contacts.length === 0) {
        delete submitValue.contacts
      }
      if (submitValue.address) {
        const address = submitValue.address
        const keys = Object.keys(address) as (keyof typeof address)[]
        keys.forEach((key) => {
          if (!address[key]) {
            delete address[key]
          }
        })
        if (Object.keys(address).length === 0) {
          delete submitValue.address
        }
      }
      updateDistributorMutation.mutate([submitValue])
    }
    catch (error) {
      console.error(error)
    }
  }

  const formOpts = formOptions({
    defaultValues:
      {
        name: '',
        timezone: dayjs.tz.guess(),
        desp: '',
        remarks: '',
        user: {
          id: '',
          username: '',
          firstName: '',
          lastName: '',
          email: '',
          locked: false,
          clientId: '',
          new: false,
          static: false,
        },
        address: {
          line1: '',
          line2: '',
          line3: '',
          city: '',
          state: '',
          countryId: '',
          postcode: '',
          type: '',
        },
        contacts: [] as Contact[],
        personsInCharge: [] as PersonsInCharge[],
        version: 0,
        id: 0,
        code: '',
      },
    validatorAdapter: zodValidator(),
    validators: {
      onChange: updateDistributorValidation,
    },
    onSubmit: async ({ value }) => {
      try {
        submitDistributor(value)
      }
      catch (error) {
        console.error('Update Distributor failed:', error)
        toast.error(`Error Submitting: ${error}`, { position: 'top-center', hideProgressBar: false, autoClose: 500 })
      }
    },
  })
  const formControl = useForm(formOpts)
  useEffect(() => {
    if (data) {
      // Initialize all form fields with empty values or data values
      const formValues = {
        name: data.name ?? '',
        timezone: data.timezone ?? dayjs.tz.guess(),
        desp: data.desp ?? '',
        remarks: data.remarks ?? '',
        address: {
          line1: data.address?.line1 ?? '',
          line2: data.address?.line2 ?? '',
          line3: data.address?.line3 ?? '',
          city: data.address?.city ?? '',
          state: data.address?.state ?? '',
          countryId: data.address?.countryId ?? '',
          postcode: data.address?.postcode ?? '',
          type: data.address?.type ?? '',
        },
        contacts: data.contacts ?? [],
        personsInCharge: data.personsInCharge ?? [],
        user: data.user ?? {},
        version: data.version ?? 0,
        id: data.id ?? 0,
        code: data.code ?? '',
      }

      // Set all form values at once
      Object.entries(formValues).forEach(([key, value]) => {
        formControl.setFieldValue(key as any, value)
      })
    }
  }, [data])

  const handleInputChange = (e: any) => {
    const { value } = e.target
    state$.newAdminEmail.set(value)
    // Run validation on each change
    try {
      emailSchema.parse(value)
      state$.emailError.set(null) // Clear error if valid
    }
    catch (e: any) {
      state$.emailError.set(e.errors[0].message) // Show the first validation error
    }
  }
  const handleSubmit = () => {
    try {
      // Attempt to validate the current email value
      emailSchema.parse(state$.newAdminEmail.get())
      state$.emailError.set(null) // Clear any existing error if validation passes
      changeEmailMutation.mutate({ id, email: state$.newAdminEmail.get() })
    }
    catch (e: any) {
      // If validation fails, set the error message
      state$.emailError.set(e.errors[0].message)
      console.error('Form submission blocked due to validation error:', e.errors[0].message)
    }
  }
  const handleClose = () => {
    state$.isOpen.set(false)
    state$.newAdminEmail.set('')
    state$.emailError.set(null)
  }
  useEffect(() => {
    fetchCountryList()
  }, [])
  const [value, setValue] = useState(['item-1', 'item-2', 'item-3', 'item-4', 'item-5'])
  useEffect(() => {
    const validateFields = async () => {
      const fieldMeta = formControl.state.fieldMeta
      if (fieldMeta && formControl.state.errors.length > 0) {
        const fieldsToValidate = Object.keys(fieldMeta) as string[]
        const validationResults = await Promise.all(
          fieldsToValidate.map(async (field) => {
            const result = await formControl.validateField(field as any, 'change')
            return { field, result }
          }),
        )

        for (const { field, result: errors } of validationResults) {
          if (errors.length > 0) {
            const fieldElement = document.getElementById(field)
            fieldElement?.focus()
            // Scroll to the field element if it exists
            fieldElement?.scrollIntoView({ behavior: 'smooth', block: 'center' })
            break
          }
        }
      }
    }

    validateFields()
  }, [formControl.state.errors])

  return (
    <div>

      <div className="flex items-center justify-between p-2">
        <div className="p-2">
          <IconButton
            color="muted"
            onClick={() => router.history.back()}
          >
            <Icon icon="lucide:arrow-left" />
          </IconButton>

          <span className="mb-4 ml-4 text-2xl font-bold">Update Distributor Page</span>
        </div>

        {/* <div className="grid grid-cols-2 gap-2"> */}
        <div>
          <Computed>
            {
              () => {
                return (
                  <>
                    <DialogComponent
                      isOpen={state$.isOpen.get()}
                      submitButtonLabel="Save Changes"
                      title="Change Admin Email"
                      onClose={handleClose}
                      onSubmit={handleSubmit}
                    >

                      <div className="relative">
                        <Label
                          required
                          className="text-xs"
                          htmlFor="adminEmail"
                        >
                          New Email
                        </Label>

                        <Input
                          className="mt-2"
                          id="adminEmail"
                          type="email"
                          value={state$.newAdminEmail.get()}
                          onChange={handleInputChange}
                        />

                        {state$.emailError.get() && <span className="text-xs text-red-500">{state$.emailError.get()}</span>}

                      </div>

                    </DialogComponent>

                    <Button
                      color="muted"
                      variant="outlined"
                      onClick={() => state$.isOpen.set(true)}
                    >
                      Change Email
                    </Button>
                  </>
                )
              }
            }
          </Computed>

        </div>

      </div>

      <Card className="mt-4">
        <div className="p-2">
          <form
            noValidate
            onSubmit={
              (e) => {
                e.preventDefault()
                setValue(['item-1', 'item-2', 'item-3', 'item-4', 'item-5'])
                formControl.handleSubmit()
              }
            }
          >
            <CardContent className="mt-4 grid gap-4">
              <Accordion
                type="multiple"
                value={value}
                onValueChange={setValue}
              >
                <AccordionItem value="item-1">
                  <AccordionTrigger>
                    <h4 className="text-lg font-semibold">Distributor Info</h4>
                  </AccordionTrigger>

                  <AccordionContent className="w-full">

                    <div className="grid grid-cols-2 gap-2">
                      <div className="grid gap-2">
                        <formControl.Field
                          name="name"
                          validators={{ onChange: updateDistributorValidation.shape.name }}
                        >
                          {
                            field => (
                              <>
                                <div className="relative">
                                  <Label
                                    required
                                    className="text-xs"
                                    htmlFor="clientName"
                                  >
                                    Name
                                  </Label>

                                  <Input
                                    id="name"
                                    type="text"
                                    value={field.state.value}
                                    onBlur={field.handleBlur}
                                    onChange={e => field.handleChange(e.target.value)}
                                  />

                                </div>

                                {
                                  field.state.meta.errors && (
                                    <em
                                      className="error-message"
                                      role="alert"
                                    >
                                      {field.state.meta.errors.join(', ')}
                                    </em>
                                  )
                                }
                              </>
                            )
                          }
                        </formControl.Field>
                      </div>

                      {/* Description Field */}
                      <div className="grid gap-2">

                        <formControl.Field name="timezone">
                          {
                            field => (
                              <>
                                <div className="relative">
                                  <Label
                                    className="text-xs"
                                    htmlFor="clientTimezone"
                                  >
                                    Timezone
                                  </Label>

                                  <Dropdown
                                    id="timezone"
                                    label=""
                                    value={field.state.value}
                                    options={
                                      Intl.supportedValuesOf('timeZone').map(timeZone => ({
                                        label: timeZone,
                                        value: timeZone,
                                      }))
                                    }
                                    onValueChange={
                                      (e) => {
                                        if (e !== '') {
                                          field.handleChange(e)
                                          field.validate('change')
                                        }
                                      }
                                    }
                                  />

                                </div>

                                {
                                  field.state.meta.errors && (
                                    <em
                                      className="error-message"
                                      role="alert"
                                    >
                                      {field.state.meta.errors.join(', ')}
                                    </em>
                                  )
                                }
                              </>
                            )
                          }
                        </formControl.Field>
                      </div>

                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      {/* Description Field */}
                      <div className="grid gap-2">

                        <formControl.Field name="desp">
                          {
                            field => (
                              <>
                                <div className="relative">
                                  <Label
                                    className="text-xs"
                                    htmlFor="clientDesp"
                                  >
                                    Description
                                  </Label>

                                  <Textarea
                                    id="desp"
                                    placeholder="Type your Description here."
                                    rows={2}
                                    value={field.state.value}
                                    onBlur={field.handleBlur}
                                    onChange={e => field.handleChange(e.target.value)}
                                  />

                                </div>

                                {
                                  field.state.meta.errors && (
                                    <em
                                      className="error-message"
                                      role="alert"
                                    >
                                      {field.state.meta.errors.join(', ')}
                                    </em>
                                  )
                                }
                              </>
                            )
                          }
                        </formControl.Field>
                      </div>

                      {/* Remarks Field */}
                      <div className="grid gap-2">

                        <formControl.Field name="remarks">
                          {
                            field => (
                              <>
                                <div className="relative">
                                  <Label
                                    className="text-xs"
                                    htmlFor="clientRemarks"
                                  >
                                    Remarks
                                  </Label>

                                  <Textarea
                                    id="remarks"
                                    placeholder="Type your Remarks here."
                                    rows={2}
                                    value={field.state.value}
                                    onBlur={field.handleBlur}
                                    onChange={e => field.handleChange(e.target.value)}
                                  />
                                </div>

                                {
                                  field.state.meta.errors && (
                                    <em
                                      className="error-message"
                                      role="alert"
                                    >
                                      {field.state.meta.errors.join(', ')}
                                    </em>
                                  )
                                }
                              </>
                            )
                          }
                        </formControl.Field>
                      </div>
                    </div>

                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-2">
                  <AccordionTrigger>
                    <h4 className="text-lg font-semibold">User Login Info</h4>
                  </AccordionTrigger>

                  <AccordionContent className="w-full">
                    <div className="grid grid-cols-2 gap-2">
                      <div className="grid gap-2">
                        <div className="relative">
                          <Label
                            required
                            className="text-xs"
                            htmlFor="firstName"
                          >
                            First Name
                          </Label>

                          <Input
                            disabled
                            id="firstName"
                            type="text"
                            value={data?.user?.firstName || ''}
                          />
                        </div>
                      </div>

                      <div className="grid gap-2">
                        <div className="relative">
                          <Label
                            className="text-xs"
                            htmlFor="lastName"
                          >
                            Last Name
                          </Label>

                          <Input
                            disabled
                            id="lastName"
                            type="text"
                            value={data?.user?.lastName || ''}
                          />
                        </div>
                      </div>

                      <div className="grid gap-2">
                        <div className="relative">
                          <Label
                            required
                            className="text-xs"
                            htmlFor="email"
                          >
                            Email
                          </Label>

                          <Input
                            disabled
                            id="email"
                            type="email"
                            value={data?.user?.email || ''}
                          />
                        </div>
                      </div>

                      <div className="grid gap-2">
                        <div className="relative">
                          <Label
                            required
                            className="text-xs"
                            htmlFor="username"
                          >
                            Username
                          </Label>

                          <Input
                            disabled
                            id="username"
                            type="text"
                            value={data?.user?.username || ''}
                          />
                        </div>

                      </div>

                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-3">
                  <AccordionTrigger>
                    <h4 className="text-xl font-semibold">Address</h4>
                  </AccordionTrigger>

                  <AccordionContent className="w-full">
                    <div className="grid">
                      <formControl.Field
                        name="address.line1"
                      >
                        {
                          field => (
                            <>
                              <div className="relative">
                                <Label
                                  required
                                  className="text-xs"
                                  htmlFor="addressLine1"
                                >
                                  Address Line 1
                                </Label>

                                <Input
                                  id="address.line1"
                                  type="text"
                                  value={field.state.value}
                                  onBlur={field.handleBlur}
                                  onChange={
                                    (e) => {
                                      field.handleChange(e.target.value), field.validate('change')
                                    }
                                  }
                                />

                              </div>

                              {
                                field.state.meta.errors && (
                                  <em
                                    className="error-message"
                                    role="alert"
                                  >
                                    {field.state.meta.errors.join(', ')}
                                  </em>
                                )
                              }
                            </>
                          )
                        }
                      </formControl.Field>
                    </div>

                    <div className="grid">
                      <formControl.Field
                        name="address.line2"
                      >
                        {
                          field => (
                            <>
                              <div className="relative">
                                <Label
                                  className="text-xs"
                                  htmlFor="addressLine2"
                                >
                                  Address Line 2
                                </Label>

                                <Input
                                  id="address.line2"
                                  type="text"
                                  value={field.state.value}
                                  onBlur={field.handleBlur}
                                  onChange={
                                    (e) => {
                                      field.handleChange(e.target.value), field.validate('change')
                                    }
                                  }
                                />

                              </div>

                              {
                                field.state.meta.errors && (
                                  <em
                                    className="error-message"
                                    role="alert"
                                  >
                                    {field.state.meta.errors.join(', ')}
                                  </em>
                                )
                              }
                            </>
                          )
                        }
                      </formControl.Field>
                    </div>

                    <div className="grid">
                      <formControl.Field
                        name="address.line3"
                      >
                        {
                          field => (
                            <>
                              <div className="relative">
                                <Label
                                  className="text-xs"
                                  htmlFor="addressLine3"
                                >
                                  Address Line 3
                                </Label>

                                <Input
                                  id="address.line3"
                                  type="text"
                                  value={field.state.value}
                                  onBlur={field.handleBlur}
                                  onChange={
                                    (e) => {
                                      field.handleChange(e.target.value), field.validate('change')
                                    }
                                  }
                                />

                              </div>

                              {
                                field.state.meta.errors && (
                                  <em
                                    className="error-message"
                                    role="alert"
                                  >
                                    {field.state.meta.errors.join(', ')}
                                  </em>
                                )
                              }
                            </>
                          )
                        }
                      </formControl.Field>
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                      <div className="grid">
                        <formControl.Field
                          name="address.postcode"
                        >
                          {
                            field => (
                              <>
                                <div className="relative">
                                  <Label
                                    required
                                    className="text-xs"
                                    htmlFor="addressPostcode"
                                  >
                                    Postcode
                                  </Label>

                                  <Input
                                    id="address.postcode"
                                    type="text"
                                    value={field.state.value}
                                    onBlur={field.handleBlur}
                                    onChange={
                                      (e) => {
                                        field.handleChange(e.target.value), field.validate('change')
                                      }
                                    }
                                  />

                                </div>

                                {
                                  field.state.meta.errors && (
                                    <em
                                      className="error-message"
                                      role="alert"
                                    >
                                      {field.state.meta.errors.join(', ')}
                                    </em>
                                  )
                                }
                              </>
                            )
                          }
                        </formControl.Field>
                      </div>

                      <div className="grid">
                        <formControl.Field
                          name="address.city"
                        >
                          {
                            field => (
                              <>
                                <div className="relative">
                                  <Label
                                    required
                                    className="text-xs"
                                    htmlFor="addressCity"
                                  >
                                    City
                                  </Label>

                                  <Input
                                    id="address.city"
                                    type="text"
                                    value={field.state.value}
                                    onBlur={field.handleBlur}
                                    onChange={
                                      (e) => {
                                        field.handleChange(e.target.value), field.validate('change')
                                      }
                                    }
                                  />

                                </div>

                                {
                                  field.state.meta.errors && (
                                    <em
                                      className="error-message"
                                      role="alert"
                                    >
                                      {field.state.meta.errors.join(', ')}
                                    </em>
                                  )
                                }
                              </>
                            )
                          }
                        </formControl.Field>
                      </div>

                      <div className="grid">
                        <formControl.Field
                          name="address.state"
                        >
                          {
                            field => (
                              <>
                                <div className="relative">
                                  <Label
                                    required
                                    className="text-xs"
                                    htmlFor="address.state"
                                  >
                                    State
                                  </Label>

                                  <Input
                                    id="address.state"
                                    type="text"
                                    value={field.state.value}
                                    onBlur={field.handleBlur}
                                    onChange={
                                      (e) => {
                                        field.handleChange(e.target.value), field.validate('change')
                                      }
                                    }
                                  />

                                </div>

                                {
                                  field.state.meta.errors && (
                                    <em
                                      className="error-message"
                                      role="alert"
                                    >
                                      {field.state.meta.errors.join(', ')}
                                    </em>
                                  )
                                }
                              </>
                            )
                          }
                        </formControl.Field>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-3">

                      <div className="grid">
                        <formControl.Field
                          name="address.countryId"
                        >
                          {
                            field => (
                              <>
                                <Computed>
                                  {
                                    () => (
                                      <div className="relative">
                                        <Label
                                          required
                                          className="text-xs"
                                          htmlFor="addressCountry"
                                        >
                                          Country
                                        </Label>

                                        <Dropdown
                                          id="address.countryId"
                                          label=""
                                          options={countryList}
                                          value={field.state.value}
                                          onValueChange={
                                            (e) => {
                                              if (e !== '') {
                                                field.handleChange(e)
                                                field.validate('change')
                                              }
                                            }
                                          }
                                        />

                                      </div>
                                    )
                                  }
                                </Computed>

                                {
                                  field.state.meta.errors && (
                                    <em
                                      className="error-message"
                                      role="alert"
                                    >
                                      {field.state.meta.errors.join(', ')}
                                    </em>
                                  )
                                }
                              </>
                            )
                          }
                        </formControl.Field>
                      </div>

                      <div className="grid">
                        <formControl.Field
                          name="address.type"
                        >
                          {
                            field => (
                              <>
                                <div className="relative">
                                  <Label
                                    required
                                    className="text-xs"
                                    htmlFor="addressType"
                                  >
                                    Type
                                  </Label>

                                  <Dropdown
                                    id="address.type"
                                    label=""
                                    options={addressTypes}
                                    value={field.state.value}
                                    onValueChange={
                                      (e) => {
                                        if (e !== '') {
                                          field.handleChange(e)
                                          field.validate('change')
                                        }
                                      }
                                    }
                                  />

                                </div>

                                {
                                  field.state.meta.errors && (
                                    <em
                                      className="error-message"
                                      role="alert"
                                    >
                                      {field.state.meta.errors.join(', ')}
                                    </em>
                                  )
                                }
                              </>
                            )
                          }
                        </formControl.Field>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-4">
                  <AccordionTrigger>
                    <h4 className="text-xl font-semibold">Client Contact</h4>
                  </AccordionTrigger>

                  <AccordionContent className="w-full">
                    <formControl.Field
                      mode="array"
                      name="contacts"
                    >
                      {
                        (field) => {
                          return (
                            <div className="w-full">
                              {
                                field.state.value
                                && field.state.value.length > 0 && (
                                  <div className="my-2 grid grid-cols-2 gap-2">
                                    <div className="grid gap-2">
                                      <Label
                                        required
                                        className="text-xs"
                                      >
                                        Contact Type
                                      </Label>
                                    </div>

                                    <div className="grid gap-2">
                                      <Label
                                        required
                                        className="text-xs"
                                      >
                                        Contact
                                      </Label>
                                    </div>
                                  </div>
                                )
                              }

                              {
                                field.state.value
                                && field.state.value.map((_, j) => {
                                  return (
                                    <div className="grid grid-cols-2 gap-2">
                                      <div className="grid gap-2">
                                        <formControl.Field
                                          key={j}
                                          name={`contacts[${j}].type`}
                                        >
                                          {
                                            (subField) => {
                                              return (
                                                <div className="relative flex flex-row items-center self-center">
                                                  <span className="mr-2">{`${j + 1}.`}</span>

                                                  <div className="relative w-full">
                                                    <Dropdown
                                                      id={`contacts[${j}].type`}
                                                      label=""
                                                      options={contactTypes}
                                                      value={subField.state.value}
                                                      onValueChange={
                                                        (e) => {
                                                          if (e !== '') {
                                                            subField.handleChange(e)
                                                            subField.validate('change')
                                                            formControl.setFieldValue(`contacts[${j}].contactVal`, '')
                                                            formControl.setFieldValue(`contacts[${j}].contactLocalVal`, '')
                                                          }
                                                        }
                                                      }
                                                    />

                                                    {
                                                      subField.state.meta.errors && (
                                                        <em
                                                          className="error-message"
                                                          role="alert"
                                                        >
                                                          {
                                                            subField.state.meta.errors.join(
                                                              ', ',
                                                            )
                                                          }
                                                        </em>
                                                      )
                                                    }
                                                  </div>
                                                </div>
                                              )
                                            }
                                          }
                                        </formControl.Field>
                                      </div>

                                      <div className="grid gap-2">
                                        <formControl.Field
                                          key={j}
                                          name={`contacts[${j}].contactVal`}
                                        >
                                          {
                                            (subSubField) => {
                                              return (
                                                <>

                                                  <div className="relative w-full">
                                                    <div className="relative flex w-full flex-row items-center self-center">
                                                      {
                                                        field.state.value[j].type === 'MOBILE' || field.state.value[j].type === 'BUSINESS_PHONE'
                                                          ? (
                                                              <PhoneInput
                                                                international
                                                                className="mt-1 flex-1"
                                                                defaultCountry="MY"
                                                                id={`contacts[${j}].contactVal`}
                                                                value={subSubField.state.value}
                                                                onChange={
                                                                  (value: { fullNumber: string, localNumber: string }) => {
                                                                    subSubField.handleChange(value.fullNumber)
                                                                    formControl.setFieldValue(`contacts[${j}].contactLocalVal`, value.localNumber)
                                                                    formControl.validate('change')
                                                                    // Validate using localNumber
                                                                    subSubField.validate('change')
                                                                  }
                                                                }
                                                              />
                                                            )
                                                          : (
                                                              <Input
                                                                className="mt-1"
                                                                disabled={field.state.value[j].type === ''}
                                                                id={`contacts[${j}].contactVal`}
                                                                type="text"
                                                                value={
                                                                  subSubField.state.value
                                                                }
                                                                onBlur={
                                                                  subSubField.handleBlur
                                                                }
                                                                onChange={
                                                                  (e) => {
                                                                    subSubField.handleChange(
                                                                      e.target.value,
                                                                    )
                                                                    subSubField.validate(
                                                                      'change',
                                                                    )
                                                                  }
                                                                }
                                                              />
                                                            )
                                                      }

                                                      <span className="ml-2">
                                                        <IconButton
                                                          className="rounded-full"
                                                          color="muted"
                                                          type="button"
                                                          variant="text"
                                                          onClick={
                                                            () =>
                                                              field.removeValue(j)
                                                          }
                                                        >
                                                          <Icon
                                                            color="red"
                                                            icon="lucide:trash-2"
                                                          />
                                                        </IconButton>
                                                      </span>

                                                    </div>

                                                    {
                                                      subSubField.state.meta
                                                        .errors && (
                                                        <em
                                                          className="error-message"
                                                          role="alert"
                                                        >
                                                          {
                                                            subSubField.state.meta.errors.join(
                                                              ', ',
                                                            )
                                                          }
                                                        </em>
                                                      )
                                                    }
                                                  </div>

                                                </>
                                              )
                                            }
                                          }
                                        </formControl.Field>
                                      </div>

                                    </div>
                                  )
                                })
                              }

                              <Button
                                className="my-2 rounded-md  border border-slate-400 bg-transparent  hover:opacity-100"
                                color="muted"
                                onClick={
                                  () =>
                                    field.pushValue({ type: '', contactVal: '', contactLocalVal: '' })
                                }
                              >
                                Add Contact
                              </Button>
                            </div>
                          )
                        }
                      }
                    </formControl.Field>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-5">
                  <AccordionTrigger>
                    <h4 className="text-xl font-semibold">Person In Charge</h4>
                  </AccordionTrigger>

                  <AccordionContent className="w-full">
                    <formControl.Field
                      mode="array"
                      name="personsInCharge"
                    >
                      {
                        (field) => {
                          return (
                            <>
                              <Button
                                className="mt-2 rounded-md border border-slate-400  bg-transparent hover:opacity-100"
                                color="muted"
                                onClick={() => field.pushValue({ firstName: '', lastName: '', contacts: [] })}
                              >
                                Add person
                              </Button>

                              <div className="mt-4">

                                {
                                  field.state.value
                                  && field.state.value.map((_, i) => {
                                    return (
                                      <>

                                        <div className="gap-2">
                                          <div className="flex items-center space-x-2">
                                            <div className="text-base font-semibold">
                                              {`${i + 1}.`}
                                            </div>

                                            <div className="flex-1">
                                              <span className="ml-2 mt-4 text-base font-semibold">PIC Details</span>
                                            </div>

                                            <div className="">
                                              <IconButton
                                                className="rounded-full"
                                                color="muted"
                                                type="button"
                                                variant="text"
                                                onClick={() => field.removeValue(i)}
                                              >
                                                <Icon
                                                  color="red"
                                                  icon="lucide:trash-2"
                                                />
                                              </IconButton>
                                            </div>
                                          </div>

                                          <div className="ml-2 mt-2 w-full">
                                            <div className="grid grid-cols-2 gap-2">
                                              <div className="grid gap-2">
                                                <formControl.Field
                                                  key={i}
                                                  name={`personsInCharge[${i}].firstName`}
                                                >
                                                  {
                                                    (subField) => {
                                                      return (
                                                        <>
                                                          <div className="relative">
                                                            <Label
                                                              className="text-xs"
                                                              htmlFor={`personsInCharge[${i}].firstName`}
                                                            >
                                                              First Name
                                                            </Label>

                                                            <Input
                                                              id={`personsInCharge[${i}].firstName`}
                                                              type="text"
                                                              value={subField.state.value}
                                                              onBlur={subField.handleBlur}
                                                              onChange={e => subField.handleChange(e.target.value)}
                                                            />

                                                          </div>

                                                          {
                                                            subField.state.meta.errors && (
                                                              <em
                                                                className="error-message"
                                                                role="alert"
                                                              >
                                                                {subField.state.meta.errors.join(', ')}
                                                              </em>
                                                            )
                                                          }
                                                        </>
                                                      )
                                                    }
                                                  }
                                                </formControl.Field>
                                              </div>

                                              <div className="grid gap-2">
                                                <formControl.Field
                                                  key={i}
                                                  name={`personsInCharge[${i}].lastName`}
                                                >
                                                  {
                                                    (subField) => {
                                                      return (
                                                        <>
                                                          <div className="relative">
                                                            <Label
                                                              className="text-xs"
                                                              htmlFor={`personsInCharge[${i}].lastName`}
                                                            >
                                                              Last Name
                                                            </Label>

                                                            <Input
                                                              id={`personsInCharge[${i}].lastName`}
                                                              type="text"
                                                              value={subField.state.value}
                                                              onBlur={subField.handleBlur}
                                                              onChange={e => subField.handleChange(e.target.value)}
                                                            />

                                                          </div>

                                                          {
                                                            subField.state.meta.errors && (
                                                              <em
                                                                className="error-message"
                                                                role="alert"
                                                              >
                                                                {subField.state.meta.errors.join(', ')}
                                                              </em>
                                                            )
                                                          }
                                                        </>
                                                      )
                                                    }
                                                  }
                                                </formControl.Field>
                                              </div>
                                            </div>

                                            <span className="text-base font-semibold">PIC Contact</span>

                                            <formControl.Field
                                              mode="array"
                                              name={`personsInCharge[${i}].contacts`}
                                            >
                                              {
                                                (field) => {
                                                  return (
                                                    <div className="w-full">
                                                      {
                                                        field.state.value.length
                                                        > 0 && (
                                                          <div className="my-2 grid grid-cols-2 gap-2">
                                                            <div className="grid gap-2">
                                                              <Label
                                                                required
                                                                className="text-xs"
                                                              >
                                                                Contact Type
                                                              </Label>
                                                            </div>

                                                            <div className="grid gap-2">
                                                              <Label
                                                                required
                                                                className="text-xs"
                                                              >
                                                                Contact
                                                              </Label>
                                                            </div>
                                                          </div>
                                                        )
                                                      }

                                                      {
                                                        field.state.value
                                                        && field.state.value.map(
                                                          (_, j) => {
                                                            return (
                                                              <div className="grid grid-cols-2 gap-2">
                                                                <div className="grid gap-2">
                                                                  <formControl.Field
                                                                    key={i}
                                                                    name={`personsInCharge[${i}].contacts[${j}].type`}
                                                                  >
                                                                    {
                                                                      (
                                                                        subSubField,
                                                                      ) => {
                                                                        return (
                                                                          <>
                                                                            <div className="relative flex flex-row items-center self-center">
                                                                              <span className="mr-2">{`${j + 1}.`}</span>

                                                                              <div className="relative w-full">
                                                                                <Dropdown
                                                                                  id={`personsInCharge[${i}].contacts[${j}].type`}
                                                                                  label=""
                                                                                  options={
                                                                                    contactTypes
                                                                                  }
                                                                                  value={
                                                                                    subSubField
                                                                                      .state
                                                                                      .value
                                                                                  }
                                                                                  onValueChange={
                                                                                    (e) => {
                                                                                      if (e !== '') {
                                                                                        subSubField.handleChange(
                                                                                          e,
                                                                                        )
                                                                                        formControl.setFieldValue(`personsInCharge[${i}].contacts[${j}].contactVal`, '')
                                                                                        formControl.setFieldValue(`personsInCharge[${i}].contacts[${j}].contactLocalVal`, '')
                                                                                      }
                                                                                    }
                                                                                  }
                                                                                />

                                                                                {
                                                                                  subSubField
                                                                                    .state
                                                                                    .meta
                                                                                    .errors && (
                                                                                    <em
                                                                                      className="error-message"
                                                                                      role="alert"
                                                                                    >
                                                                                      {
                                                                                        subSubField.state.meta.errors.join(
                                                                                          ', ',
                                                                                        )
                                                                                      }
                                                                                    </em>
                                                                                  )
                                                                                }
                                                                              </div>
                                                                            </div>

                                                                          </>
                                                                        )
                                                                      }
                                                                    }
                                                                  </formControl.Field>
                                                                </div>

                                                                <div className="grid gap-2">
                                                                  <formControl.Field
                                                                    key={j}
                                                                    name={`personsInCharge[${i}].contacts[${j}].contactVal`}
                                                                  >
                                                                    {
                                                                      (
                                                                        subSubField,
                                                                      ) => {
                                                                        return (
                                                                          <>

                                                                            <div className="relative w-full">
                                                                              <div className="relative flex w-full flex-row items-center self-center">
                                                                                {
                                                                                  field.state.value[j].type === 'MOBILE' || field.state.value[j].type === 'BUSINESS_PHONE'
                                                                                    ? (
                                                                                        <PhoneInput
                                                                                          international
                                                                                          className="mt-1 flex-1"
                                                                                          defaultCountry="MY"
                                                                                          id={`personsInCharge[${i}].contacts[${j}].contactVal`}
                                                                                          value={subSubField.state.value}
                                                                                          onChange={
                                                                                            (value: { fullNumber: string, localNumber: string }) => {
                                                                                              subSubField.handleChange(value.fullNumber)
                                                                                              formControl.setFieldValue(`personsInCharge[${i}].contacts[${j}].contactLocalVal`, value.localNumber)

                                                                                              // Validate using localNumber
                                                                                              subSubField.validate('change')
                                                                                            }
                                                                                          }
                                                                                        />
                                                                                      )
                                                                                    : (
                                                                                        <Input
                                                                                          className="mt-1"
                                                                                          id={`personsInCharge[${i}].contacts[${j}].contactVal`}
                                                                                          type="text"
                                                                                          value={subSubField.state.value}
                                                                                          onBlur={subSubField.handleBlur}
                                                                                          onChange={e => subSubField.handleChange(e.target.value)}
                                                                                        />
                                                                                      )
                                                                                }

                                                                                <span className="ml-2">
                                                                                  <IconButton
                                                                                    className="rounded-full"
                                                                                    color="muted"
                                                                                    type="button"
                                                                                    variant="text"
                                                                                    onClick={
                                                                                      () =>
                                                                                        field.removeValue(
                                                                                          j,
                                                                                        )
                                                                                    }
                                                                                  >
                                                                                    <Icon
                                                                                      color="red"
                                                                                      icon="lucide:trash-2"
                                                                                    />
                                                                                  </IconButton>
                                                                                </span>
                                                                              </div>

                                                                              {
                                                                                subSubField
                                                                                  .state
                                                                                  .meta
                                                                                  .errors && (
                                                                                  <em
                                                                                    className="error-message"
                                                                                    role="alert"
                                                                                  >
                                                                                    {
                                                                                      subSubField.state.meta.errors.join(
                                                                                        ', ',
                                                                                      )
                                                                                    }
                                                                                  </em>
                                                                                )
                                                                              }
                                                                            </div>

                                                                          </>
                                                                        )
                                                                      }
                                                                    }
                                                                  </formControl.Field>
                                                                </div>

                                                              </div>
                                                            )
                                                          },
                                                        )
                                                      }

                                                      <Button
                                                        className="my-2 rounded-md  border border-slate-400 bg-transparent  hover:opacity-100"
                                                        color="muted"
                                                        onClick={
                                                          () =>
                                                            field.pushValue({
                                                              type: '',
                                                              contactVal: '',
                                                              contactLocalVal: '',
                                                            })
                                                        }
                                                      >
                                                        Add Contact
                                                      </Button>
                                                    </div>
                                                  )
                                                }
                                              }
                                            </formControl.Field>
                                          </div>

                                        </div>

                                        {
                                          field.state.value.length !== i + 1 && (
                                            <Separator className="mb-4 mt-2" />
                                          )
                                        }
                                      </>
                                    )
                                  })
                                }
                              </div>
                            </>
                          )
                        }
                      }
                    </formControl.Field>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>

            </CardContent>

            <CardFooter>
              <formControl.Subscribe selector={state => [state.canSubmit, state.isSubmitting]}>
                {
                  ([canSubmit, isSubmitting]) => (
                    <Button
                      fullWidth
                      disabled={!auth$.privileges.get()?.has('0.3.7.2')}
                      type="submit"
                    >
                      {isSubmitting ? '...' : 'Save'}
                    </Button>
                  )
                }
              </formControl.Subscribe>
            </CardFooter>
          </form>
        </div>
      </Card>
    </div>
  )
}
